import React, { FunctionComponent, MouseEvent, ReactNode, useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { ButtonGroup, Chip, MenuItem, Typography } from "@mui/material";
import { CalendarToday, CameraAlt, ScatterPlot } from "@mui/icons-material";
import _ from "underscore";
import { UserPermissionAction } from "avvir";

import "../../../../css/nav_bar/nav_bar_scan_dropdown.css";
import DateConverter from "../../../services/converters/date_converter";
import getFloorId from "../../../services/getters/floor_getters/get_floor_id";
import getPhotoSessionIds from "../../../services/getters/photo_area_getters/get_photo_session_ids";
import getProjectIdFromLocation from "../../../services/getters/location_metadata/get_project_id_from_location";
import getScanDatasetId from "../../../services/getters/scan_dataset_getters/get_scan_dataset_id";
import NavBarDropdown from "../nav_bar_dropdown";
import ConnectedNavBarQaStatusSelector from "./nav_bar_qa_status_selector";
import ScrollableMenu from "../../shared/scrollable_menu";
import selectPhotoSession from "../../../actions/viewer_page/select_photo_session";
import TooltippedOnOverflow from "../../shared/tooltipped_on_overflow";
import toViewerScanDataset, { ToViewerScanDatasetEvent } from "../../../events/routing/to_viewer_scan_dataset";
import useFloor from "../../../queries/floors/use_floor";
import usePhotoSessionsForPhotoArea from "../../../queries/photos/use_photo_sessions_for_photo_area";
import usePopover from "../../../services/component_helpers/component_effects/use_popover";
import usePrevious from "../../../services/component_helpers/component_effects/use_previous";
import useScanDatasetsForFloor from "../../../queries/scan_datasets/use_scan_datasets_for_floor";

import type { Dispatch } from "type_aliases";
import { useProjectPermission } from "../../../queries/user/use_project_permission";

const dateFormatter = DateConverter.getDateFormatter("MMM D, YYYY");

export const NavBarScanDropdown: FunctionComponent<Props> = (props) => {
  const {
    startIcon = <CalendarToday className="NavBarScanDropdown-buttonIcon"/>,
    endIcon,
    floorId,
    projectId,
    selectedScanDatasetId,
    toViewerScanDataset,
    photoSessionIds,
    selectPhotoSession
  } = props;
  const { data: floor } = useFloor(projectId, floorId);
  const { data: scanDatasets } = useScanDatasetsForFloor(projectId, floorId);
  const { data: photoSessions } = usePhotoSessionsForPhotoArea(projectId, floor?.photoAreaId);
  const [openState, anchorEl, handleOpen, onClose] = usePopover();
  const canUpdateQAQCComplete = useProjectPermission(UserPermissionAction.UPDATE_QA_QC_COMPLETE, false, true);

  const photoSessionExistsByDate = useMemo(() => {
    return _.reduce(photoSessions, (byDate, session) => {
      const day = DateConverter.dateToUTCDayStringOrEmpty(session.sessionDate);
      if (day.length > 0) {
        byDate[day] = true;
      }
      return byDate;
    }, {});
  }, [photoSessions]);

  const selectedPhotoSessionDate = useMemo(() => {
    return photoSessions?.[photoSessionIds[0]]?.sessionDate;
  }, [photoSessions, photoSessionIds]);

  const sortedScanDatasets = useMemo(() => {
    return _.chain(scanDatasets)
      .values()
      .filter((scan) => scan.scanDate != null)
      .sortBy("scanDate")
      .value();
  }, [scanDatasets]);
  const selectedIndex = sortedScanDatasets.findIndex(scan => scan.firebaseId === selectedScanDatasetId);

  const handleSelect = useCallback((event: MouseEvent<HTMLLIElement>) => {
    const scanDatasetId = event.currentTarget.dataset.value;
    toViewerScanDataset(projectId, floorId, scanDatasetId);
    const scanDataset = scanDatasets?.[scanDatasetId];
    if (!DateConverter.isOnDay(scanDataset.scanDate, selectedPhotoSessionDate)) {
      selectPhotoSession(scanDataset.scanDate);
    }
    onClose();
  }, [toViewerScanDataset, projectId, floorId, scanDatasets, selectedPhotoSessionDate, onClose, selectPhotoSession]);
  const previousPhotoSessionDate = usePrevious(selectedPhotoSessionDate || NaN);

  useEffect(() => {
    const scanDataset = scanDatasets?.[selectedScanDatasetId];
    if (!DateConverter.isOnDay(scanDataset?.scanDate, selectedPhotoSessionDate)) {
      selectPhotoSession(scanDataset?.scanDate);
    }
    // This is to set the selected photo session on page load, so it has no dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const scanDataset = scanDatasets?.[selectedScanDatasetId];
    if (!DateConverter.isOnDay(scanDataset?.scanDate, selectedPhotoSessionDate)) {
      if (selectedPhotoSessionDate == null && previousPhotoSessionDate != null) {
        selectPhotoSession(scanDataset?.scanDate);
      } else if (selectedPhotoSessionDate != null) {
        selectPhotoSession(scanDataset?.scanDate);
      }
    }
  }, [scanDatasets, selectedScanDatasetId, selectedPhotoSessionDate, previousPhotoSessionDate, selectPhotoSession]);

  const scanDatasetsList = useMemo(() => sortedScanDatasets.map((scanDataset) => {
    let secondaryText = null;
    if (scanDataset.name) {
      secondaryText = <Typography className="NavBarScanDropdown-menuItem-scanDate" variant="label">
        {dateFormatter.formatUTC(scanDataset.scanDate)}
      </Typography>;
    }
    const scanDisplayText = scanDataset.name || dateFormatter.formatUTC(scanDataset.scanDate);
    const scanDay = DateConverter.dateToUTCDayStringOrEmpty(scanDataset.scanDate);
    const photosExist = scanDay.length > 0 && photoSessionExistsByDate[scanDay];
    return <MenuItem
      key={`NavBarScanDropdown-${scanDataset.firebaseId}`}
      className="NavBarScanDropdown-menuItem"
      selected={selectedScanDatasetId === scanDataset.firebaseId}
      onClick={handleSelect}
      data-value={scanDataset.firebaseId}
    >
      <div className="NavBarScanDropdown-menuItem-container">
        <div className="NavBarScanDropdown-menuItem-subtypeIcon">
          {photosExist ? <Chip icon={<CameraAlt/>} label="Photo" size="small"/> : null}
          {scanDataset.hasScanFile ? <Chip icon={<ScatterPlot/>} label="Scan" size="small"/> : null}
        </div>
        <div className="NavBarScanDropdown-menuItem-displayText">
          <TooltippedOnOverflow>
            {scanDisplayText}
          </TooltippedOnOverflow>
          {secondaryText}
        </div>
      </div>
    </MenuItem>;
  }), [sortedScanDatasets, photoSessionExistsByDate, selectedScanDatasetId, handleSelect]);

  const selectedScanDataset = scanDatasets[selectedScanDatasetId];
  const scanDatasetsButton = <NavBarDropdown
    className="NavBarScanDropdown-button"
    label={<TooltippedOnOverflow>
      <span className="NavBarScanDropdown-buttonText">
        {selectedScanDataset?.name || dateFormatter.formatUTC(selectedScanDataset?.scanDate)}
      </span>
    </TooltippedOnOverflow>}
    popoverProps={{
      open: openState,
      anchorEl,
      handleOpen,
      onClose
    }}
    slots={{ menu: ScrollableMenu }}
    slotProps={{
      button: {
        "aria-label": "Scan Dataset Menu Button",
        startIcon,
        ...endIcon && { endIcon },
      },
      menu: {
        selectedIndex,
        className: "NavBarScanDropdown"
      }
    }}
  >
    {scanDatasetsList}
  </NavBarDropdown>;

  if (canUpdateQAQCComplete) {
    return <ButtonGroup
      className="NavBarScanDropdown-button"
      variant="contained"
      disableElevation
      size="medium"
      color="secondary"
    >
      {scanDatasetsButton}
      <ConnectedNavBarQaStatusSelector projectId={projectId} floorId={floorId} scanDatasetId={selectedScanDatasetId} endIcon={endIcon}/>
    </ButtonGroup>;
  } else {
    return scanDatasetsButton;
  }
};

const mapStateToProps = (state, props) => ({
  projectId: getProjectIdFromLocation(state, props),
  floorId: getFloorId(state, props),
  photoSessionIds: getPhotoSessionIds(state, props),
  selectedScanDatasetId: getScanDatasetId(state, props),
});

type DispatchedTypes = ToViewerScanDatasetEvent
                       | ReturnType<typeof selectPhotoSession>
const mapDispatchToProps = (dispatch: Dispatch<DispatchedTypes>) => bindActionCreators({
  toViewerScanDataset,
  selectPhotoSession
}, dispatch);

// Overridable so the second-level nav can use the Nova glyph its design calls for; the default
// keeps the legacy nav unchanged.
export type OwnProps = { startIcon?: ReactNode, endIcon?: ReactNode };

export type Props = OwnProps & ReturnType<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps, mapDispatchToProps)(NavBarScanDropdown);
